import React from "react";

import { ButtonStandard, ModalBase, Skeleton, Text } from "@performyard/py-components";

import { AssigneeLabel } from "./components/AssigneeLabel";

import { useSignatureRequestTemplateDetail } from "../../../api/hooks/defs/signature-request-templates";

import { type AssigneeValue, getAssigneeWildcard } from "./launch-form";

/**
 * The detail endpoint reports signers as the wildcards "self" | "manager" | "other" or,
 * for a step pinned to one person, that person's user id — with no user map. The row's
 * own signer values (seeded from the same steps) carry the resolved names, so they take
 * precedence; the raw reference is only a fallback.
 */
const resolveSigner = (rawSigner: string | null, rowSigner: AssigneeValue | null | undefined): AssigneeValue | null => {
	if (rowSigner) return rowSigner;
	if (!rawSigner || rawSigner === "other") return null;
	const wildcard = getAssigneeWildcard(rawSigner);
	return wildcard ? { id: wildcard.id, name: wildcard.name } : { id: rawSigner, name: "Unknown employee" };
};

export const SignatureTemplateDetailsModal = ({
	templateId,
	templateName,
	signers: rowSigners,
	onClose,
	onRemove,
}: {
	templateId: string;
	templateName: string;
	/** The to-do row's current signer values, index-aligned with the template's steps. */
	signers?: (AssigneeValue | null)[];
	onClose: () => void;
	onRemove: () => void;
}) => {
	const { data, isLoading } = useSignatureRequestTemplateDetail(templateId);

	const steps = data?.signers ?? [];

	return (
		<ModalBase isOpen onClose={onClose} dangerouslySetClassName="w-[432px] max-w-[calc(100vw-32px)]">
			<div className="flex flex-col gap-4 px-6 pb-6">
				<div className="flex flex-col">
					<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
						<span className="py-line-clamp-1">{data?.name ?? templateName}</span>
					</Text>
					<Text size="paragraph-2" weight="regular" color="neutral-9" dangerouslySetClassName="text-center">
						Signature Steps
					</Text>
				</div>

				{isLoading ? (
					<div className="flex flex-col gap-2" data-testid="signature-steps-loading">
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</div>
				) : steps.length === 0 ? (
					<Text size="paragraph-3" weight="regular" color="neutral-6">
						This template has no signature steps.
					</Text>
				) : (
					<ol
						className="m-0 flex list-none flex-col divide-y divide-solid divide-neutral-2 p-0"
						data-testid="signature-steps"
					>
						{steps.map((step, index) => {
							const signer = resolveSigner(step.signer, rowSigners?.[index]);
							return (
								// Signature steps are ordered and have no ids of their own.
								<li key={index} className="flex items-start gap-2 p-2">
									<Text
										size="paragraph-2"
										weight="regular"
										color="neutral-9"
										dangerouslySetClassName="w-7 shrink-0"
									>
										{`${index + 1}.`}
									</Text>
									<div className="flex min-w-0 flex-1 flex-col gap-1">
										<div className="flex h-7 items-center">
											{signer ? (
												<AssigneeLabel assignee={signer} />
											) : (
												<Text size="paragraph-3" weight="regular" color="neutral-6">
													Unassigned
												</Text>
											)}
										</div>
										{step.fields.length > 0 ? (
											step.fields.map((field, fieldIndex) => (
												// Field names can repeat within a step; the order is what identifies them.
												<Text
													key={fieldIndex}
													size="caption"
													weight="regular"
													color="neutral-6"
												>
													<span className="py-line-clamp-1">{`· ${field}`}</span>
												</Text>
											))
										) : (
											<Text size="caption" weight="regular" color="neutral-5">
												· No Signature Instructions
											</Text>
										)}
									</div>
								</li>
							);
						})}
					</ol>
				)}

				<div className="flex justify-center gap-2 pt-2">
					<div className="w-fit">
						<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div className="w-fit">
						<ButtonStandard
							variant="solid"
							color="red"
							size="large"
							onClick={onRemove}
							data-testid="remove-signature-template"
						>
							Remove Signature Request
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};
